import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Box,
  Container,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Test, testAPI } from '../services/apiService';
import { testOrdering } from '../utils/localStorage';
import { InputBase } from '@mui/material';
import { useDrag } from '../contexts/DragContext';

// Local storage constants
const LOCAL_STORAGE_TESTS_CACHE_KEY = 'interviewzen-tests-cache';
const CACHE_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes

// Styled components
const TestCardContainer = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isDragging' && prop !== 'isTemplate' && prop !== 'isSyncing'
})<{ isDragging?: boolean; isTemplate?: boolean; isSyncing?: boolean }>(({ theme, isDragging, isTemplate, isSyncing }) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  cursor: isSyncing ? 'wait' : 'pointer',
  transition: 'all 0.3s ease-in-out',
  marginBottom: theme.spacing(2),
  backgroundColor: isTemplate ? '#f8f9fa' : (isSyncing ? '#f0f8ff' : '#ffffff'),
  border: isTemplate ? '2px dashed #0985E3' : (isSyncing ? '2px solid #0985E3' : '1px solid #e0e0e0'),
  opacity: isSyncing ? 0.8 : 1,
  transform: isDragging ? 'scale(1.02)' : 'scale(1)',
  zIndex: isDragging ? 1000 : 1,
  boxShadow: isDragging 
    ? '0px 8px 16px rgba(0, 0, 0, 0.2)' 
    : '0px 2px 4px rgba(0, 0, 0, 0.1)',
  
  '&:hover': {
    transform: isDragging ? 'scale(1.02)' : 'scale(1.02)',
    boxShadow: isDragging 
      ? '0px 8px 16px rgba(0, 0, 0, 0.2)' 
      : '0px 4px 8px rgba(0, 0, 0, 0.15)',
  }
}));

const TestGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: theme.spacing(3),
  width: '100%'
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(6),
  backgroundColor: '#f8f9fa',
  borderRadius: theme.spacing(1),
  border: '2px dashed #e0e0e0',
  marginTop: theme.spacing(4)
}));

// Template test interface for inline creation
interface TemplateTest {
  uuid: string;
  name: string;
  date_created: string;
  isTemplate: true;
}

type TestOrTemplate = Test | TemplateTest;

const TestsPage: React.FC = () => {
  const navigate = useNavigate();
  const { registerHandler, unregisterHandler } = useDrag();
  const [tests, setTests] = useState<Test[]>([]);
  const [orderedTests, setOrderedTests] = useState<TestOrTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [testToDelete, setTestToDelete] = useState<Test | null>(null);
  
  // Template creation state
  const [isCreatingTest, setIsCreatingTest] = useState(false);
  const [templateTest, setTemplateTest] = useState<TemplateTest | null>(null);
  const [editingTestId, setEditingTestId] = useState<string | null>(null);
  
  // Navigation state for optimistic tests
  const [pendingNavigationTestId, setPendingNavigationTestId] = useState<string | null>(null);
  const [syncingTestIds, setSyncingTestIds] = useState<Set<string>>(new Set());

  // Local storage helper functions
  const loadCachedTests = (): { tests: Test[], timestamp: number } | null => {
    try {
      const cachedData = localStorage.getItem(LOCAL_STORAGE_TESTS_CACHE_KEY);
      if (cachedData) {
        return JSON.parse(cachedData);
      }
      return null;
    } catch (error) {
      console.error('Error loading cached tests from local storage:', error);
      return null;
    }
  };

  const saveTestsToCache = (testsToCache: Test[]) => {
    try {
      const cacheData = {
        tests: testsToCache,
        timestamp: Date.now()
      };
      localStorage.setItem(LOCAL_STORAGE_TESTS_CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error saving tests to cache:', error);
    }
  };

  const isCacheValid = (timestamp: number): boolean => {
    return Date.now() - timestamp < CACHE_MAX_AGE_MS;
  };

  useEffect(() => {
    // Load cached tests immediately if available
    const cachedData = loadCachedTests();
    if (cachedData && isCacheValid(cachedData.timestamp)) {
      setTests(cachedData.tests);
      setIsLoading(false);
    }
    
    // Then load tests from API (in background if we have cache)
    loadTests().catch(error => {
      console.error("Failed to load tests:", error);
    });
  }, []);

  // Define drag handler before using it in useEffect
  const handleDragEnd = useCallback((result: DropResult) => {
    const { source, destination } = result;

    // Dropped outside the list or no change
    if (!destination || source.index === destination.index) {
      return;
    }

    // Check if we're trying to drag a template item or temporary test
    const sourceItem = orderedTests[source.index];
    const destinationItem = orderedTests[destination.index];
    
    if ('isTemplate' in sourceItem || ('isTemplate' in destinationItem)) {
      return; // Don't allow dragging template items or dropping on them
    }
    
    // Don't allow dragging temporary tests that are still syncing
    if (sourceItem.uuid.startsWith('temp-') || destinationItem.uuid.startsWith('temp-')) {
      console.log('🚫 Blocking drag operation on temporary test');
      return;
    }

    // Work directly with the real tests for reordering
    const realTests = tests.slice(); // Copy current tests array
    const sourceTestIndex = realTests.findIndex(test => test.uuid === sourceItem.uuid);
    const destinationTestIndex = realTests.findIndex(test => test.uuid === (destinationItem as Test).uuid);
    
    if (sourceTestIndex === -1 || destinationTestIndex === -1) {
      return; // Invalid test indices
    }

    // Reorder the tests array
    const [movedTest] = realTests.splice(sourceTestIndex, 1);
    realTests.splice(destinationTestIndex, 0, movedTest);

    // Update tests state
    setTests(realTests);

    // Update localStorage with new order
    const newOrder = realTests.map(test => test.uuid);
    testOrdering.setOrder(newOrder);
  }, [orderedTests, tests]);

  // Register drag handler on mount
  useEffect(() => {
    registerHandler('tests-grid', handleDragEnd);
    
    return () => {
      unregisterHandler('tests-grid');
    };
  }, [registerHandler, unregisterHandler, handleDragEnd]);

  // Apply localStorage ordering when tests change
  useEffect(() => {
    if (tests.length > 0) {
      const orderedTestsList = testOrdering.applyOrder(tests);
      const testsWithTemplate = templateTest 
        ? [templateTest, ...orderedTestsList] 
        : orderedTestsList;
      setOrderedTests(testsWithTemplate);
    } else {
      const testsWithTemplate = templateTest ? [templateTest] : [];
      setOrderedTests(testsWithTemplate);
    }
  }, [tests, templateTest]);

  const loadTests = async (forceRefresh = false) => {
    try {
      setError(null);
      
      // If force refresh or no tests in state, don't use cache
      if (forceRefresh || tests.length === 0) {
        setIsLoading(true);
      }
      
      // Try to load from cache first (unless force refresh)
      if (!forceRefresh) {
        const cachedData = loadCachedTests();
        
        if (cachedData && isCacheValid(cachedData.timestamp)) {
          // Use cached data immediately
          setTests(cachedData.tests);
          setIsLoading(false);
          
          // Still refresh in background to ensure consistency
          try {
            const fetchedTests = await testAPI.getAll();
            setTests(fetchedTests);
            saveTestsToCache(fetchedTests);
          } catch (bgError) {
            console.error('Background refresh failed:', bgError);
            // Keep using cached data
          }
          return;
        }
      }
      
      // Cache invalid or doesn't exist, do a regular fetch
      const fetchedTests = await testAPI.getAll();
      setTests(fetchedTests);
      saveTestsToCache(fetchedTests);
      
    } catch (err: any) {
      setError(err.message || 'Failed to load tests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTest = () => {
    console.log('🔥 BUTTON CLICKED - CODE IS RUNNING!');
    console.log('🆕 Create Test button clicked');
    if (isCreatingTest) {
      console.log('⚠️ Already creating test, returning');
      return;
    }
    
    const newTemplate: TemplateTest = {
      uuid: `template-${Date.now()}`,
      name: '',
      date_created: new Date().toISOString(),
      isTemplate: true
    };
    
    console.log('📝 Creating template:', newTemplate);
    setTemplateTest(newTemplate);
    setIsCreatingTest(true);
    setEditingTestId(newTemplate.uuid);
    console.log('✅ Template state updated');
  };

  const handleSaveNewTest = async (name: string) => {
    if (!name.trim()) {
      handleCancelNewTest();
      return;
    }

    try {
      setError(null);
      
      // Generate optimistic test with temporary ID
      const optimisticTest: Test = {
        uuid: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: name.trim(),
        date_created: new Date().toISOString(),
        time_limit_minutes: 45
      };
      
      // Track that this test is syncing
      setSyncingTestIds(prev => new Set(prev).add(optimisticTest.uuid));

      // Optimistic update: Add test to UI immediately
      setTests(prevTests => {
        const updatedTests = [optimisticTest, ...prevTests];
        saveTestsToCache(updatedTests);
        return updatedTests;
      });

      // Add to localStorage order at the beginning
      testOrdering.addTest(optimisticTest.uuid);
      
      // Clean up template state (test now shows in list)
      setTemplateTest(null);
      setIsCreatingTest(false);
      setEditingTestId(null);

      // Sync with backend in background
      console.log('🚀 Making API call to create test:', { name: name.trim() });
      const createdTest = await testAPI.create({ name: name.trim() });
      console.log('✅ API call successful, created test:', createdTest);
      
      if (createdTest) {
        // Backend success - replace optimistic test with real test
        console.log('🔄 Replacing optimistic test with real test');
        
        // Small delay to ensure any drag operations complete before replacing
        setTimeout(() => {
          setTests(prevTests => {
            const updatedTests = prevTests.map(t => 
              t.uuid === optimisticTest.uuid ? createdTest : t
            );
            saveTestsToCache(updatedTests);
            return updatedTests;
          });
          
          // Update localStorage order - replace temp ID with real ID
          testOrdering.removeTest(optimisticTest.uuid);
          testOrdering.addTest(createdTest.uuid);
          
          // Check if user wanted to navigate to this test
          if (pendingNavigationTestId === optimisticTest.uuid) {
            console.log('🚀 Auto-navigating to real test after sync:', createdTest.uuid);
            navigate(`/tests/${createdTest.uuid}`);
            setPendingNavigationTestId(null);
          }
          
          // Remove from syncing set
          setSyncingTestIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(optimisticTest.uuid);
            return newSet;
          });
        }, 100); // Small delay to prevent drag-and-drop conflicts
      } else {
        console.log('❌ API returned null/undefined');
        throw new Error('Failed to create test on server');
      }
      
    } catch (err: any) {
      console.log('💥 Error in handleSaveNewTest:', err);
      setError(err.message || 'Failed to create test');
      
      // Revert optimistic update on error
      console.log('🔙 Reverting optimistic update');
      setTests(prevTests => {
        const revertedTests = prevTests.filter(t => !t.uuid.startsWith('temp-'));
        saveTestsToCache(revertedTests);
        return revertedTests;
      });
      
      // Clear pending navigation and syncing state
      if (pendingNavigationTestId === optimisticTest.uuid) {
        setPendingNavigationTestId(null);
      }
      setSyncingTestIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(optimisticTest.uuid);
        return newSet;
      });
      
      // Show template again for retry
      const retryTemplate: TemplateTest = {
        uuid: `template-${Date.now()}`,
        name: name,
        date_created: new Date().toISOString(),
        isTemplate: true
      };
      setTemplateTest(retryTemplate);
      setIsCreatingTest(true);
      setEditingTestId(retryTemplate.uuid);
    }
  };

  const handleCancelNewTest = () => {
    setTemplateTest(null);
    setIsCreatingTest(false);
    setEditingTestId(null);
    // Clear any pending navigation
    setPendingNavigationTestId(null);
  };

  const handleTestNameChange = async (test: Test, newName: string) => {
    if (!newName.trim() || newName === test.name) {
      return;
    }

    try {
      setError(null);
      const updatedTest = await testAPI.update(test.uuid, { name: newName.trim() });
      
      // Update local state
      setTests(prevTests => 
        prevTests.map(t => t.uuid === test.uuid ? updatedTest : t)
      );
      
    } catch (err: any) {
      setError(err.message || 'Failed to update test name');
    }
  };

  const handleCardClick = (test: TestOrTemplate) => {
    if ('isTemplate' in test || editingTestId === test.uuid) {
      return; // Don't navigate if it's a template or being edited
    }
    
    // Check if this is a temporary optimistic test
    if (test.uuid.startsWith('temp-')) {
      console.log('🚫 Blocking navigation to temporary test:', test.uuid);
      // Set pending navigation - will auto-navigate after backend sync
      setPendingNavigationTestId(test.uuid);
      return;
    }
    
    // Normal navigation for real tests
    navigate(`/tests/${test.uuid}`);
  };

  const handleDeleteClick = (event: React.MouseEvent, test: Test) => {
    event.stopPropagation();
    setTestToDelete(test);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!testToDelete) return;
    
    try {
      setError(null);
      await testAPI.delete(testToDelete.uuid);
      
      // Remove from localStorage order
      testOrdering.removeTest(testToDelete.uuid);
      
      // Remove from local state
      setTests(prevTests => prevTests.filter(test => test.uuid !== testToDelete.uuid));
      
      setDeleteDialogOpen(false);
      setTestToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete test');
      setDeleteDialogOpen(false);
      setTestToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTestToDelete(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1">
            My Tests
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateTest}
            disabled={isCreatingTest}
          >
            Create Test
          </Button>
        </Box>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
            {error.includes('500') && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Server error occurred. Please try again later or contact support if the issue persists.
                </Typography>
              </Box>
            )}
          </Alert>
        )}

        {/* Loading State */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Tests Grid with Drag & Drop */}
            {orderedTests.length === 0 ? (
              <EmptyStateBox>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  You don't have any tests yet
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Click "Create Test" above to get started
                </Typography>
              </EmptyStateBox>
            ) : (
              <Droppable droppableId="tests-grid">
                {(provided, snapshot) => (
                    <TestGrid
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      sx={{
                        backgroundColor: snapshot.isDraggingOver ? 'rgba(9, 133, 227, 0.05)' : 'transparent',
                        transition: 'background-color 0.2s ease',
                        borderRadius: 1,
                        padding: snapshot.isDraggingOver ? 2 : 0,
                      }}
                    >
                      {orderedTests.map((test, index) => {
                        const isTemplate = 'isTemplate' in test;
                        const isBeingEdited = editingTestId === test.uuid;
                        const isSyncing = !isTemplate && test.uuid.startsWith('temp-') && syncingTestIds.has(test.uuid);
                        const isPendingNavigation = pendingNavigationTestId === test.uuid;
                        
                        // For drag-and-drop stability, disable dragging for temporary tests
                        // This prevents UUID conflicts during optimistic -> real test replacement
                        
                        return (
                          <Draggable
                            key={test.uuid}
                            draggableId={test.uuid}
                            index={index}
                            isDragDisabled={isTemplate || isBeingEdited || isSyncing}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...provided.draggableProps.style,
                                }}
                              >
                                <TestCardContainer
                                  isDragging={snapshot.isDragging}
                                  isTemplate={isTemplate}
                                  isSyncing={isSyncing}
                                  onClick={() => handleCardClick(test)}
                                >
                                  {/* Delete Button - Only for real tests */}
                                  {!isTemplate && (
                                    <IconButton
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        zIndex: 2,
                                        padding: 0.5,
                                        transition: 'all 0.2s ease-in-out',
                                        color: 'text.secondary',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        '&:hover': {
                                          color: 'error.main',
                                          backgroundColor: 'rgba(255, 255, 255, 1)',
                                          transform: 'scale(1.1)',
                                        },
                                      }}
                                      size="small"
                                      onClick={(event) => handleDeleteClick(event, test as Test)}
                                      aria-label="delete"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  )}

                                  <CardContent sx={{ pt: isTemplate ? 2 : 5, pb: 2 }}>
                                    {/* Test Name - Simple InputBase like Telemore */}
                                    {isTemplate ? (
                                      <Box sx={{ mb: 2 }}>
                                        <InputBase
                                          value={test.name}
                                          onChange={(e) => {
                                            console.log('📝 Template input changed:', e.target.value);
                                            setTemplateTest(prev => prev ? { ...prev, name: e.target.value } : null);
                                          }}
                                          onKeyDown={(e) => {
                                            console.log('⌨️ Key pressed:', e.key);
                                            e.stopPropagation();
                                            if (e.key === 'Escape') {
                                              console.log('🚫 Escape pressed - canceling');
                                              handleCancelNewTest();
                                            } else if (e.key === 'Enter') {
                                              console.log('✅ Enter pressed - saving');
                                              handleSaveNewTest(test.name);
                                            }
                                          }}
                                          placeholder="Enter test name..."
                                          autoFocus
                                          sx={{
                                            fontSize: '1.5rem',
                                            fontWeight: 500,
                                            width: '100%',
                                            padding: '6px 8px',
                                            border: '1px solid #e0e0e0',
                                            borderRadius: '4px',
                                            backgroundColor: '#ffffff',
                                            '&:focus': {
                                              outline: 'none',
                                              boxShadow: '0 0 0 2px rgba(9, 133, 227, 0.2)',
                                            }
                                          }}
                                        />
                                      </Box>
                                    ) : (
                                      <Box sx={{ mb: 2 }}>
                                        {isBeingEdited ? (
                                          <InputBase
                                            value={test.name}
                                            onChange={(e) => {
                                              // Update test name directly for existing tests
                                              console.log('📝 Editing test name:', e.target.value);
                                            }}
                                            onKeyDown={(e) => {
                                              console.log('⌨️ Key pressed on existing test:', e.key);
                                              e.stopPropagation();
                                              if (e.key === 'Escape') {
                                                console.log('🚫 Escape pressed - canceling edit');
                                                setEditingTestId(null);
                                              } else if (e.key === 'Enter') {
                                                console.log('✅ Enter pressed - saving existing test');
                                                handleTestNameChange(test as Test, e.target.value);
                                                setEditingTestId(null);
                                              }
                                            }}
                                            onBlur={(e) => {
                                              console.log('👋 Input blurred - saving existing test');
                                              handleTestNameChange(test as Test, e.target.value);
                                              setEditingTestId(null);
                                            }}
                                            placeholder="Test name..."
                                            autoFocus
                                            sx={{
                                              fontSize: '1.5rem',
                                              fontWeight: 500,
                                              width: '100%',
                                              padding: '6px 8px',
                                              border: '1px solid #e0e0e0',
                                              borderRadius: '4px',
                                              backgroundColor: '#ffffff',
                                              '&:focus': {
                                                outline: 'none',
                                                boxShadow: '0 0 0 2px rgba(9, 133, 227, 0.2)',
                                              }
                                            }}
                                          />
                                        ) : (
                                          <Box
                                            onClick={() => {
                                              console.log('🖱️ Clicked to edit test:', test.name);
                                              setEditingTestId(test.uuid);
                                            }}
                                            sx={{
                                              fontSize: '1.5rem',
                                              fontWeight: 500,
                                              padding: '6px 8px',
                                              borderRadius: '4px',
                                              cursor: 'text',
                                              '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                              }
                                            }}
                                          >
                                            {test.name}
                                          </Box>
                                        )}
                                      </Box>
                                    )}

                                    {/* Created Date / Sync Status */}
                                    {!isTemplate && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" color="textSecondary">
                                          {isSyncing ? 'Creating...' : `Created: ${formatDate(test.date_created)}`}
                                        </Typography>
                                        {isSyncing && (
                                          <CircularProgress size={16} sx={{ ml: 1 }} />
                                        )}
                                        {isPendingNavigation && (
                                          <Typography variant="caption" color="primary" sx={{ fontStyle: 'italic' }}>
                                            Click to open when ready
                                          </Typography>
                                        )}
                                      </Box>
                                    )}

                                    {/* Template Instructions */}
                                    {isTemplate && (
                                      <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                        Type a name and press Enter to create your test
                                      </Typography>
                                    )}
                                    
                                    {/* Pending Navigation Hint */}
                                    {isPendingNavigation && !isTemplate && (
                                      <Typography variant="body2" color="primary" sx={{ fontStyle: 'italic', mt: 1 }}>
                                        📌 Will open automatically when ready...
                                      </Typography>
                                    )}
                                  </CardContent>
                                </TestCardContainer>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </TestGrid>
                  )}
                </Droppable>
            )}
          </>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Delete Test</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete "{testToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TestsPage;
